import {Component, Input, signal, inject} from '@angular/core';
import {
  NbButtonModule,
  NbCardModule,
  NbDatepickerModule,
  NbDialogRef,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbOptionModule,
  NbProgressBarModule,
  NbRadioModule,
  NbSelectModule,
  NbSpinnerModule,
  NbToastrService
} from "@nebular/theme";
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {HttpEventType} from "@angular/common/http";
import {Router} from "@angular/router";
import {MatchesService} from "../../../../../services/matches.service";
import {notificationsParams} from "../../../../../shared/settings";
import {TranslateModule, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-report-game-form',
  standalone: true,
  imports: [
    TranslateModule,
    NbButtonModule,
    NbCardModule,
    NbIconModule,
    ReactiveFormsModule,
    NbOptionModule,
    NbSelectModule,
    NbDatepickerModule,
    NbRadioModule,
    ReactiveFormsModule,
    NbLayoutModule,
    NbInputModule,
    NbSpinnerModule,
    NbProgressBarModule
  ],
  templateUrl: './report-game-form.component.html',
  styleUrl: './report-game-form.component.scss'
})
export class ReportGameFormComponent {
  private readonly translate = inject(TranslateService);
  reportGameForm!: FormGroup;
  // selectedFiles: File[] = []; // screenshots désactivés
  selectedVideos: File[] = [];
  // isDragOver = false; // screenshots désactivés
  isVideoDragOver = false;

  @Input() match!: any;

  loading = signal<boolean>(false);
  uploadProgress = signal<number | null>(null);
  scoreOptions = signal<any[]>([]);
  videoSettings = signal<{ max_size_mb: number; max_duration_seconds: number; allowed_types: string[] }>({
    max_size_mb: 100,
    max_duration_seconds: 30,
    allowed_types: ['video/mp4', 'video/x-matroska', 'video/x-msvideo', 'video/avi', 'video/quicktime', 'video/x-ms-wmv']
  });

  constructor(private fb: FormBuilder, private matchesService: MatchesService, private router: Router, private toastrService: NbToastrService, protected dialogRef: NbDialogRef<any>) {}

  ngOnInit(): void {
    this.reportGameForm = this.fb.group({
      match_id: [this.match.match_id, [Validators.required]],
      team_1_score: [this.match.team_1_score, Validators.required],
      team_2_score: [this.match.team_2_score, Validators.required],
      is_playoffs: [this.match?.is_playoffs || false, Validators.required],
      // screenshots: [''], // screenshots désactivés
    });

    this.loading.set(false);

    this.matchesService.scoreOptions('').subscribe(scoreOptions => {
      this.loading.set(false);
      this.scoreOptions.set(scoreOptions.map((value: number) => ({ code: value, name: value})));
    });

    this.matchesService.videoSettings().subscribe(settings => {
      this.videoSettings.set(settings);
    });
  }

  videoAccept(): string {
    return '.mkv,.avi,.mp4,.mov,.wmv';
  }

  // -- Screenshots désactivés --
  // onDragOver(event: DragEvent) {
  //   event.preventDefault();
  //   event.stopPropagation();
  //   this.isDragOver = true;
  // }

  // onDragLeave(event: DragEvent) {
  //   event.preventDefault();
  //   event.stopPropagation();
  //   this.isDragOver = false;
  // }

  // onDrop(event: DragEvent) {
  //   event.preventDefault();
  //   event.stopPropagation();
  //   this.isDragOver = false;
  //   const files = event.dataTransfer?.files;
  //   if (files) { this.addFiles(files); }
  // }
  // -- Fin screenshots désactivés --

  onVideoDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isVideoDragOver = true;
  }

  onVideoDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isVideoDragOver = false;
  }

  onVideoDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isVideoDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.addVideoFiles(files);
    }
  }

  // -- Screenshots désactivés --
  // onFileSelect(event: Event) {
  //   const input = event.target as HTMLInputElement;
  //   if (input.files) {
  //     if ((input.files.length + this.selectedFiles.length) > this.match.nb_max_screenshots) {
  //       this.toastrService.danger(`You can't upload more than ${this.match.nb_max_screenshots} screenshots`, this.translate.instant('common.error'), notificationsParams);
  //       return;
  //     }
  //     this.addFiles(input.files);
  //     this.reportGameForm.patchValue({ screenshots: input.files });
  //   }
  // }
  // -- Fin screenshots désactivés --

  onVideoSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addVideoFiles(input.files);
    }
  }

  // -- Screenshots désactivés --
  // addFiles(fileList: FileList) {
  //   const files = Array.from(fileList).filter(file => file.type.startsWith('image/'));
  //   this.selectedFiles.push(...files);
  // }
  // -- Fin screenshots désactivés --

  private getFileType(file: File): string {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'mkv') return 'video/x-matroska';
    if (file.type && file.type !== 'application/octet-stream') return file.type;
    const extMap: Record<string, string> = {
      mp4: 'video/mp4', avi: 'video/x-msvideo',
      mov: 'video/quicktime', wmv: 'video/x-ms-wmv'
    };
    return ext ? (extMap[ext] ?? '') : '';
  }

  async addVideoFiles(fileList: FileList) {
    const settings = this.videoSettings();
    for (const file of Array.from(fileList)) {
      const fileType = this.getFileType(file);
      if (!settings.allowed_types.includes(fileType)) {
        this.toastrService.danger(`${file.name}: unsupported format (allowed: mkv, avi, mp4, mov, wmv)`, this.translate.instant('common.error'), notificationsParams);
        continue;
      }
      if (file.size > settings.max_size_mb * 1024 * 1024) {
        this.toastrService.danger(`${file.name} exceeds ${settings.max_size_mb} MB`, this.translate.instant('common.error'), notificationsParams);
        continue;
      }
      const duration = await this.getVideoDuration(file);
      if (duration !== null && isFinite(duration) && Math.floor(duration) > settings.max_duration_seconds) {
        this.toastrService.danger(`${file.name} exceeds ${settings.max_duration_seconds}s (${Math.round(duration)}s)`, this.translate.instant('common.error'), notificationsParams);
        continue;
      }
      this.selectedVideos.push(file);
    }
  }

  private getVideoDuration(file: File): Promise<number | null> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => { URL.revokeObjectURL(video.src); resolve(null); };
      video.src = URL.createObjectURL(file);
    });
  }

  // -- Screenshots désactivés --
  // removeFile(index: number) {
  //   this.selectedFiles.splice(index, 1);
  // }
  // -- Fin screenshots désactivés --

  removeVideo(index: number) {
    this.selectedVideos.splice(index, 1);
  }

  onCancel() {
    this.reportGameForm.patchValue({
      team_1_score: 0,
      team_2_score: 0,
      // screenshots: [], // screenshots désactivés
    });
    // this.selectedFiles = []; // screenshots désactivés
    this.selectedVideos = [];
    this.dialogRef.close();
  }

  onSubmit() {
    const data = this.reportGameForm.value;
    const formData = new FormData();
    formData.append('match_id', data.match_id);
    formData.append('is_playoffs', data?.is_playoffs || false);
    formData.append('team_1_score', data.team_1_score);
    formData.append('team_2_score', data.team_2_score);
    // -- Screenshots désactivés --
    // this.selectedFiles.forEach((file) => {
    //   formData.append('screenshots[]', file, file.name);
    // });
    // -- Fin screenshots désactivés --
    this.selectedVideos.forEach((file) => {
      formData.append('videos[]', file, file.name);
    });

    this.uploadProgress.set(0);

    this.matchesService.updateScore(data.match_id, formData).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.uploadProgress.set(Math.round(100 * event.loaded / event.total));
        } else if (event.type === HttpEventType.Response) {
          this.uploadProgress.set(null);
          this.toastrService.success(this.translate.instant('games.match.score_updated'), this.translate.instant('common.information'), notificationsParams);
          this.dialogRef.close();
        }
      },
      error: (e: any) => {
        this.uploadProgress.set(null);
        const message = e?.error?.message || e?.message;
        this.toastrService.danger(message, this.translate.instant('common.error'), notificationsParams);
      }
    });
  }

  getControl(controlName: string): FormControl {
    return this.reportGameForm.get(controlName) as FormControl;
  }
}
